<template>
  <v-container>
    <div class="back-nav">
      <!--        Intentionally Empty-->
    </div>
    <div class="header">
      <h1 class="mb-2 page-title">
        <v-icon class="mb-2">$home</v-icon>
        Home
      </h1>
    </div>
    <div class="enrollment-widget mb-4" v-if="unfinishedEnrollments.length">
      <v-card>
        <v-container>
          <v-row>
            <v-icon small class="back-to-enrollment-icon">$employees</v-icon>
            <span class="back-to-enrollment-text"
              >It looks like you did not complete enrollment for one or more
              clients.</span
            >
            <v-spacer></v-spacer>

            <yellow-button
              v-if="unfinishedEnrollments.length === 1"
              class="back-to-enrollment-button"
              @click="enterSignup(unfinishedEnrollments[0].signupCode)"
              >Complete Enrollment
            </yellow-button>
            <dropdown
              v-else
              :options="
                unfinishedEnrollments.map(({ customerName, signupCode }) => ({
                  key: signupCode,
                  text: customerName
                }))
              "
              @click="enterSignup($event)"
            >
              <yellow-button class="back-to-enrollment-button"
                >Complete Enrollment
              </yellow-button>
            </dropdown>
          </v-row>
        </v-container>
      </v-card>
    </div>
    <payment-waiver-status />
    <v-row>
      <v-col cols="12" md="6">
        <pt-sheet title="Customers">
          <template
            v-slot:header
            v-if="!this.isFeatureEnabled('MAINTENANCE_MODE')"
          >
            <signup-code-dialog-button />
          </template>
          <v-divider></v-divider>
          <div>
            <v-card
              v-for="(customer, index) in customers.slice(0, maxListLength)"
              :key="`customer-${customer.clientId}`"
              flat
            >
              <v-card-text class="d-flex py-6 align-center">
                <div class="status-icon">
                  <v-icon
                    >{{
                      compliant(customer) ? "$goodStanding" : "$needsAttention"
                    }}
                  </v-icon>
                </div>
                <div class="customer-content flex-grow-1 pa-2">
                  <div class="card-main-text">{{ customer.customerName }}</div>
                  <div class="card-subtext">
                    {{ customer.complianceStatus }}
                  </div>
                </div>
                <div class="actions">
                  <v-btn
                    v-if="customer.enrollmentComplete"
                    height="32px"
                    min-width="32px"
                    :to="'customers/' + customer.clientId + '/detail'"
                    class="tertiary"
                    depressed
                    @click.native="
                      logEvent('Clicked on Customer', {
                        Client: customer.customerName
                      })
                    "
                  >
                    <v-icon color="primary">mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </v-card-text>
              <v-divider v-if="index !== customers.length - 1"></v-divider>
            </v-card>
          </div>
          <div class="pb-3 float-right">
            <v-btn
              small
              v-if="customers.length > maxListLength"
              text
              to="/customers"
            >
              <div class="button-text">
                View all ({{ customers.length - maxListLength }} not shown)
                <v-icon right>mdi-arrow-right</v-icon>
              </div>
            </v-btn>
          </div>
        </pt-sheet>
      </v-col>
      <v-col cols="12" md="6">
        <pt-sheet title="To-Dos">
          <v-divider></v-divider>
          <div>
            <div v-if="!checklistItems" class="no-data text-center ma-6">
              <v-icon class="ma-4">$allCaughtUp</v-icon>
              <div class="card-main-text font-weight-medium">
                You're all caught up!
              </div>
            </div>
            <v-card
              v-for="(checklistItem, index) in checklistItems.slice(
                0,
                maxListLength
              )"
              :key="checklistItem.id"
              flat
            >
              <v-card-text class="d-flex py-6 align-center">
                <div class="status-icon">
                  <v-icon>$needsAttention</v-icon>
                </div>
                <div class="customer-content flex-grow-1 pa-2">
                  <div class="card-main-text">
                    <span
                      v-if="
                        checklistItem.completionStatus &&
                          checklistItem.completionStatus.name ===
                            'REVISION_NEEDED'
                      "
                      >Revision Needed -
                    </span>
                    {{ checklistItem.name }}
                  </div>
                  <div class="card-subtext">
                    For {{ checklistItem.clientName }}
                  </div>
                </div>
                <div v-if="checklistItem.dueDate" class="due-date mr-4">
                  DUE {{ checklistItem.dueDate | formatDate }}
                </div>
                <div class="actions">
                  <v-btn
                    height="32px"
                    min-width="32px"
                    :to="getLink(checklistItem)"
                    @click.native="
                      logEvent('Clicked on to do item', {
                        ItemName: checklistItem.name,
                        Client: checklistItem.clientName
                      })
                    "
                    class="tertiary"
                    depressed
                  >
                    <v-icon color="primary">mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </v-card-text>
              <v-divider v-if="index !== checklistItems.length - 1"></v-divider>
            </v-card>
            <v-card
              v-if="checklistItems.length > maxListLength"
              flat
              class="text-right mt-4"
            >
              <v-btn text to="/customers">
                <div class="button-text">
                  All To-Dos by customer
                  <v-icon right>mdi-arrow-right</v-icon>
                </div>
              </v-btn>
            </v-card>
          </div>
        </pt-sheet>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PtSheet from "../components/PtSheet.vue";
import Dropdown from "@/components/Dropdown";
import PaymentWaiverStatus from "../components/PaymentWaiverStatus";
import { checklistItemAPI, customersAPI } from "@/services/api";
import { get } from "@/services/request";
import format from "date-fns/format";
import { SERVER_DATE_FORMAT } from "@/services/constants";
import { getChecklistItemLink } from "@/services/util";

import YellowButton from "@/components/providertrust/buttons/SmallYellowButton";
import SignupCodeDialogButton from "@/components/SignupCodeDialogButton.vue";
import hasNotifications from "@/mixins/has-notifications";

const maxListLength = 4;

export default {
  components: {
    SignupCodeDialogButton,
    PtSheet,
    YellowButton,
    PaymentWaiverStatus,
    Dropdown
  },
  mixins: [hasNotifications],
  created() {
    this.clearMessage();
    this.loadData();
    this.logEvent("Landed on home page");
  },
  data: () => ({
    maxListLength,
    customers: [],
    checklistItems: [],
    whereData: { Where: "vendor-dashboard" }
  }),
  provide() {
    return {
      Where: this.whereData.Where
    };
  },
  filters: {
    formatDate(val) {
      if (val) {
        const today = format(new Date(), SERVER_DATE_FORMAT);
        if (val === today) return "NOW";
        else {
          return val.substring(5, 7) + "/" + val.substring(8, 10);
        }
      }
    }
  },
  methods: {
    loadData() {
      get(customersAPI)
        .then(response => {
          if (response && response.data) {
            this.customers = response.data || [];
          }
        })
        .then(() =>
          get(checklistItemAPI).then(response => {
            if (response && response.data) {
              let allChecklistItems = response.data || [];
              let incompleteClients = this.unfinishedEnrollments.map(
                customer => customer.clientId
              );
              this.checklistItems = allChecklistItems.filter(
                item => !incompleteClients.includes(item.clientId)
              );
            }
          })
        );
    },
    getLink(item) {
      return getChecklistItemLink(item, item.clientId);
    },
    enterSignup(signupCode) {
      this.logEvent("Clicked button to re-enter signup");

      this.$router.push({
        name: "vendorSignup",
        params: { signupCode }
      });
    },
    compliant({ complianceStatus }) {
      return complianceStatus === "Compliant";
    }
  },
  computed: {
    unfinishedEnrollments() {
      return this.customers.filter(
        ({ enrollmentComplete }) => !enrollmentComplete
      );
    }
  }
};
</script>

<style lang="sass" scoped>
h3
  font-size: 20px
  line-height: 25px
  color: $darker-grey

.card-main-text
  font-size: 20px
  line-height: 25px
  color: $lightish-grey

.v-btn
  font-weight: bold
  letter-spacing: unset
  text-indent: unset
  font-size: 1rem

  .button-text
    border-bottom: 1px solid $primary
    color: $primary
    font-size: 14px

.back-to-enrollment-icon
  margin: 8px 8px 7px 12px

.back-to-enrollment-text
  height: 24px
  font-size: 16px
  line-height: 24px
  color: $maybe-last-grey
  margin-top: 4px

.back-to-enrollment-button
  margin-right: 12px
</style>
